"use client";

import { useMemo, useState, useSyncExternalStore } from "react";

import { ReviewedPageSchema } from "@/components/JsonLd";
import PageShell from "@/components/PageShell";

import {
  type AnswerMap,
  bandFor,
  BANDS,
  type BandTone,
  categoryBreakdown,
  DISCLAIMER,
  FAQS,
  KEY_DATES,
  MAX_RAW,
  OPEN_ITEMS,
  PENALTIES,
  phaseByKey,
  type PhaseKey,
  PHASES,
  QUESTIONS,
  type Remediation,
  remediations,
  RESEARCHED_ON,
  RESEARCHED_ON_ISO,
  scoreQuiz,
  SDF_NOTE,
} from "./_data/questions";

// ── helpers ────────────────────────────────────────────────────────────────

const TONE_COLOR: Record<BandTone, string> = {
  risk: "var(--red)",
  partial: "var(--amber)",
  ready: "var(--green)",
};

function toneForPct(pct: number): BandTone {
  if (pct <= 40) return "risk";
  if (pct <= 70) return "partial";
  return "ready";
}

type Remaining = { past: boolean; days: number; hours: number; mins: number; secs: number };

/** Ticks once a second, client-side only. The server snapshot is `null` so the
 *  prerendered HTML and the first client render agree; the countdown is then
 *  computed from the real current date on the client. Rounding to the second
 *  keeps the snapshot stable between React's render and commit reads. */
function subscribeToSecond(onStoreChange: () => void): () => void {
  const t = window.setInterval(onStoreChange, 1000);
  return () => window.clearInterval(t);
}

function secondSnapshot(): number {
  return Math.floor(Date.now() / 1000) * 1000;
}

function serverSnapshot(): null {
  return null;
}

function useNow(): number | null {
  return useSyncExternalStore(subscribeToSecond, secondSnapshot, serverSnapshot);
}

/** Commencement dates are Indian, so anchor them to IST rather than UTC. */
function remainingTo(iso: string, now: number | null): Remaining | null {
  if (now === null) return null;
  const ms = new Date(`${iso}T00:00:00+05:30`).getTime() - now;
  if (ms <= 0) return { past: true, days: 0, hours: 0, mins: 0, secs: 0 };
  const s = Math.floor(ms / 1000);
  return {
    past: false,
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    mins: Math.floor((s % 3600) / 60),
    secs: s % 60,
  };
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

// ── countdown card ─────────────────────────────────────────────────────────

function CountdownCard({
  iso,
  display,
  headline,
  sub,
  now,
}: {
  iso: string;
  display: string;
  headline: string;
  sub: string;
  now: number | null;
}) {
  const r = remainingTo(iso, now);
  return (
    <div
      style={{
        background: "var(--o950)",
        border: "1px solid rgba(251,250,246,.08)",
        borderRadius: "6px",
        padding: "22px 24px",
        color: "var(--b50)",
      }}
    >
      <div
        style={{
          fontSize: "10px",
          letterSpacing: ".18em",
          textTransform: "uppercase",
          color: "var(--cl)",
          marginBottom: "10px",
        }}
      >
        {display}
      </div>
      <div
        style={{
          fontFamily: "var(--font-fraunces), serif",
          fontSize: "44px",
          fontWeight: 200,
          letterSpacing: "-.03em",
          lineHeight: 1,
          display: "flex",
          alignItems: "baseline",
          gap: "10px",
        }}
      >
        {r === null ? "—" : r.past ? "In force" : r.days}
        {r !== null && !r.past && (
          <span style={{ fontSize: "14px", letterSpacing: 0, color: "rgba(251,250,246,.5)" }}>
            days
          </span>
        )}
      </div>
      <div
        style={{
          fontVariantNumeric: "tabular-nums",
          fontSize: "12px",
          color: "rgba(251,250,246,.45)",
          marginTop: "6px",
          letterSpacing: ".08em",
          minHeight: "18px",
        }}
      >
        {r === null ? " " : r.past ? "Commenced" : `${pad(r.hours)}:${pad(r.mins)}:${pad(r.secs)} remaining (IST)`}
      </div>
      <div
        style={{
          fontSize: "13px",
          color: "var(--b50)",
          marginTop: "14px",
          paddingTop: "12px",
          borderTop: "1px solid rgba(251,250,246,.08)",
          lineHeight: 1.5,
        }}
      >
        {headline}
      </div>
      <p style={{ fontSize: "11.5px", color: "rgba(251,250,246,.5)", lineHeight: 1.65, marginTop: "8px" }}>
        {sub}
      </p>
    </div>
  );
}

// ── page ───────────────────────────────────────────────────────────────────

export default function DpdpReadinessQuizPage() {
  const now = useNow();

  const [answers, setAnswers] = useState<AnswerMap>({});
  const [idx, setIdx] = useState(0);
  const [done, setDone] = useState(false);
  const [printedAt, setPrintedAt] = useState<string | null>(null);

  const current = QUESTIONS[idx];
  const answeredCount = useMemo(
    () => QUESTIONS.filter((q) => typeof answers[q.id] === "number").length,
    [answers]
  );
  const allAnswered = answeredCount === QUESTIONS.length;

  const result = useMemo(() => scoreQuiz(answers), [answers]);
  const cats = useMemo(() => categoryBreakdown(answers), [answers]);
  const rems = useMemo(() => remediations(answers), [answers]);
  const band = bandFor(result.score);

  const byPhase = useMemo(() => {
    const keys: PhaseKey[] = ["phase2", "phase3"];
    return keys
      .map((k) => ({ phase: phaseByKey(k), items: rems.filter((r) => r.question.phaseKey === k) }))
      .filter((g) => g.items.length > 0);
  }, [rems]);

  function choose(optionIndex: number) {
    setAnswers((prev) => ({ ...prev, [current.id]: optionIndex }));
  }

  /** Stamps the results sheet at the moment the user finishes, so the printed
   *  copy says when it was produced. Computed in the event handler rather than
   *  during render — a `new Date()` in render would differ between the
   *  prerendered HTML and the client. */
  function finish() {
    setPrintedAt(
      new Date().toLocaleString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    );
    setDone(true);
  }

  function goNext() {
    if (idx < QUESTIONS.length - 1) {
      setIdx(idx + 1);
      return;
    }
    if (allAnswered) finish();
  }

  function goBack() {
    if (done) {
      setDone(false);
      setIdx(QUESTIONS.length - 1);
      return;
    }
    if (idx > 0) setIdx(idx - 1);
  }

  function restart() {
    setAnswers({});
    setIdx(0);
    setDone(false);
    setPrintedAt(null);
  }

  const chosenIndex = answers[current.id];
  const progressPct = Math.round((answeredCount / QUESTIONS.length) * 100);

  return (
    <>
      <ReviewedPageSchema
        description="A self-assessment against the Digital Personal Data Protection Act, 2023 and its phased commencement dates."
        name="DPDP Act readiness quiz"
        path="/tools/dpdp-readiness-quiz"
        reviewedOn={RESEARCHED_ON_ISO}
      />
      <PageShell>
        <style>{`
          .dq-hero{display:grid;grid-template-columns:minmax(0,1.35fr) minmax(0,1fr);gap:clamp(28px,4vw,56px);align-items:start;padding:72px clamp(20px,4vw,52px) 56px;border-bottom:1px solid var(--b200)}
          .dq-dates{display:flex;flex-direction:column;gap:14px}
          .dq-phases{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:16px}
          .dq-catrow{display:grid;grid-template-columns:150px minmax(0,1fr) 52px;gap:14px;align-items:center}
          .dq-opt:hover{border-color:var(--cu)!important}
          .dq-pill:hover{border-color:var(--cu)!important}
          .dq-printonly{display:none}
          @media (max-width:960px){
            .dq-hero{grid-template-columns:1fr;gap:32px}
            .dq-phases{grid-template-columns:1fr}
          }
          @media (max-width:560px){
            .dq-catrow{grid-template-columns:110px minmax(0,1fr) 44px;gap:10px}
          }
          @media print{
            @page{margin:14mm}
            #nav,nav,footer,#grain,#sp,#cur,#intro,.dq-noprint{display:none!important}
            body{background:#fff!important;color:var(--o900)!important}
            .dq-printonly{display:block!important}
            .dq-results{padding:0!important;border:0!important}
            .dq-card,.dq-rem,.dq-catrow,.dq-scorepanel{break-inside:avoid;page-break-inside:avoid}
            .dq-dark-print{background:#fff!important;color:var(--o900)!important;border:1px solid var(--b300)!important}
            .dq-dark-print *{color:var(--o900)!important}
          }
        `}</style>

        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <section className="dq-hero dq-noprint">
          <div>
            <a
              href="/tools"
              style={{ fontSize: "12px", color: "rgba(19,18,16,.55)", textDecoration: "none", letterSpacing: ".05em" }}
            >
              ← All tools
            </a>
            <div className="ey rev" style={{ marginTop: "20px" }}>
              Compliance / DPDP Readiness Quiz
            </div>
            <h1 className="sh2 rev d1" style={{ fontSize: "clamp(32px,4vw,52px)" }}>
              DPDP readiness quiz.{" "}<br />
              <em>15 questions. Weighted.</em>
            </h1>
            <p
              className="rev d2"
              style={{ fontSize: "15px", color: "rgba(19,18,16,.55)", maxWidth: "560px", marginTop: "14px", lineHeight: 1.8 }}
            >
              A self-assessment against the Digital Personal Data Protection Act, 2023 and the DPDP Rules, 2025 — every
              question cited to the provision it comes from, every answer weighted by what sits behind it in THE
              SCHEDULE. Reviewed {RESEARCHED_ON}.
            </p>

            <div
              className="rev d3"
              style={{
                marginTop: "26px",
                maxWidth: "560px",
                padding: "16px 20px",
                background: "var(--b100)",
                border: "1px solid var(--b200)",
                borderLeft: "3px solid var(--cu)",
                borderRadius: "4px",
              }}
            >
              <div
                style={{
                  fontSize: "10px",
                  letterSpacing: ".18em",
                  textTransform: "uppercase",
                  color: "var(--cu)",
                  marginBottom: "8px",
                }}
              >
                Nothing leaves your browser
              </div>
              <p style={{ fontSize: "13px", color: "rgba(19,18,16,.72)", lineHeight: 1.7 }}>
                Your answers live in this tab&apos;s memory and nowhere else. No API call, no analytics event carrying
                your answers, no email gate, no stored report — refreshing clears everything. The print button hands the
                page to your own browser&apos;s print dialogue. For a privacy self-assessment that matters more than
                usual, so check the network tab if you like.
              </p>
            </div>
          </div>

          <div className="dq-dates rev d3">
            <CountdownCard
              display={KEY_DATES.consentManager.display}
              headline={KEY_DATES.consentManager.headline}
              iso={KEY_DATES.consentManager.iso}
              now={now}
              sub={KEY_DATES.consentManager.sub}
            />
            <CountdownCard
              display={KEY_DATES.fullCompliance.display}
              headline={KEY_DATES.fullCompliance.headline}
              iso={KEY_DATES.fullCompliance.iso}
              now={now}
              sub={KEY_DATES.fullCompliance.sub}
            />
          </div>
        </section>

        {/* ── Quiz / results ───────────────────────────────────────────── */}
        <section
          id="quiz"
          style={{ padding: "44px clamp(20px,4vw,52px) 64px", borderBottom: "1px solid var(--b200)", background: "var(--b100)" }}
        >
          <div style={{ maxWidth: "860px", margin: "0 auto" }}>
            {!done ? (
              <div
                className="dq-card"
                style={{
                  background: "var(--b50)",
                  border: "1px solid var(--b200)",
                  borderRadius: "6px",
                  padding: "clamp(24px,3vw,38px)",
                }}
              >
                {/* progress */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "12px", flexWrap: "wrap" }}>
                  <div style={{ fontSize: "11px", letterSpacing: ".14em", textTransform: "uppercase", color: "rgba(19,18,16,.5)" }}>
                    Question {idx + 1} of {QUESTIONS.length}
                  </div>
                  <div style={{ fontSize: "11px", color: "rgba(19,18,16,.5)" }}>
                    {answeredCount} answered · {progressPct}%
                  </div>
                </div>
                <div style={{ height: "3px", background: "var(--b200)", borderRadius: "2px", marginTop: "10px", overflow: "hidden" }}>
                  <div
                    style={{
                      height: "100%",
                      width: `${progressPct}%`,
                      background: "var(--cu)",
                      transition: "width .3s ease",
                    }}
                  />
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", marginTop: "14px" }}>
                  {QUESTIONS.map((q, i) => {
                    const isAnswered = typeof answers[q.id] === "number";
                    const isCurrent = i === idx;
                    return (
                      <button
                        key={q.id}
                        aria-current={isCurrent ? "step" : undefined}
                        aria-label={`Go to question ${i + 1}`}
                        className="dq-pill"
                        style={{
                          width: "26px",
                          height: "26px",
                          borderRadius: "3px",
                          fontSize: "10px",
                          cursor: "pointer",
                          fontFamily: "var(--font-dm), sans-serif",
                          background: isCurrent ? "var(--o900)" : isAnswered ? "rgba(184,110,44,.12)" : "transparent",
                          color: isCurrent ? "var(--b50)" : isAnswered ? "var(--cu)" : "rgba(19,18,16,.45)",
                          border: `1px solid ${isCurrent ? "var(--o900)" : isAnswered ? "rgba(184,110,44,.35)" : "var(--b200)"}`,
                          transition: "border-color .2s, background .2s",
                        }}
                        type="button"
                        onClick={() => setIdx(i)}
                      >
                        {i + 1}
                      </button>
                    );
                  })}
                </div>

                {/* chips */}
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "26px" }}>
                  <span style={chip("var(--cu)", "rgba(184,110,44,.3)", "rgba(184,110,44,.07)")}>{current.category}</span>
                  <span style={chip("rgba(19,18,16,.6)", "var(--b200)", "transparent")}>Weight ×{current.weight}</span>
                  <span
                    style={chip(
                      current.phaseKey === "phase2" ? "var(--amber)" : "rgba(19,18,16,.6)",
                      current.phaseKey === "phase2" ? "rgba(217,119,6,.3)" : "var(--b200)",
                      current.phaseKey === "phase2" ? "rgba(217,119,6,.06)" : "transparent"
                    )}
                  >
                    {`Commences ${phaseByKey(current.phaseKey).date}`}
                  </span>
                </div>

                {/* question */}
                <h2
                  style={{
                    fontFamily: "var(--font-fraunces), serif",
                    fontWeight: 200,
                    fontSize: "clamp(19px,2.2vw,26px)",
                    lineHeight: 1.38,
                    letterSpacing: "-.015em",
                    color: "var(--o900)",
                    marginTop: "16px",
                  }}
                >
                  {current.question}
                </h2>

                {/* citation — shown so the user learns the provision as they go */}
                <div
                  style={{
                    marginTop: "14px",
                    paddingLeft: "12px",
                    borderLeft: "2px solid var(--cu)",
                  }}
                >
                  <div style={{ fontSize: "9px", letterSpacing: ".16em", textTransform: "uppercase", color: "var(--cu)", marginBottom: "4px" }}>
                    Provision
                  </div>
                  <div style={{ fontSize: "12.5px", color: "rgba(19,18,16,.7)", lineHeight: 1.6 }}>{current.citation}</div>
                  <div style={{ fontSize: "11.5px", color: "rgba(19,18,16,.5)", lineHeight: 1.6, marginTop: "5px" }}>
                    {current.phase}
                  </div>
                </div>

                {/* options */}
                <div aria-label="Answer options" role="group" style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "24px" }}>
                  {current.options.map((o, i) => {
                    const active = chosenIndex === i;
                    return (
                      <button
                        key={i}
                        aria-pressed={active}
                        className="dq-opt"
                        style={{
                          display: "flex",
                          gap: "12px",
                          alignItems: "flex-start",
                          textAlign: "left",
                          padding: "14px 16px",
                          borderRadius: "4px",
                          cursor: "pointer",
                          background: active ? "rgba(184,110,44,.06)" : "var(--b50)",
                          border: `1px solid ${active ? "var(--cu)" : "var(--b200)"}`,
                          fontFamily: "var(--font-dm), sans-serif",
                          transition: "border-color .2s, background .2s",
                        }}
                        type="button"
                        onClick={() => choose(i)}
                      >
                        <span
                          aria-hidden="true"
                          style={{
                            flexShrink: 0,
                            width: "15px",
                            height: "15px",
                            borderRadius: "50%",
                            marginTop: "3px",
                            border: `1px solid ${active ? "var(--cu)" : "var(--b300)"}`,
                            background: active ? "var(--cu)" : "transparent",
                            boxShadow: active ? "inset 0 0 0 3px var(--b50)" : "none",
                          }}
                        />
                        <span style={{ fontSize: "13.5px", lineHeight: 1.65, color: active ? "var(--o900)" : "rgba(19,18,16,.78)" }}>
                          {o.label}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {typeof chosenIndex === "number" && (
                  <details
                    style={{
                      marginTop: "16px",
                      padding: "14px 16px",
                      background: "var(--b100)",
                      border: "1px solid var(--b200)",
                      borderRadius: "4px",
                    }}
                  >
                    <summary style={{ fontSize: "12.5px", fontWeight: 500, color: "var(--cu)", cursor: "pointer", listStyle: "none" }}>
                      Why this one matters →
                    </summary>
                    <p style={{ fontSize: "13px", color: "rgba(19,18,16,.72)", lineHeight: 1.8, marginTop: "10px" }}>
                      {current.guidance}
                    </p>
                  </details>
                )}

                {/* nav */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginTop: "26px", paddingTop: "20px", borderTop: "1px solid var(--b200)", flexWrap: "wrap" }}>
                  <button
                    disabled={idx === 0}
                    style={{
                      padding: "11px 22px",
                      borderRadius: "3px",
                      border: "1px solid var(--b300)",
                      background: "transparent",
                      color: idx === 0 ? "rgba(19,18,16,.3)" : "var(--o900)",
                      fontSize: "13px",
                      fontFamily: "var(--font-dm), sans-serif",
                      cursor: idx === 0 ? "not-allowed" : "pointer",
                    }}
                    type="button"
                    onClick={goBack}
                  >
                    ← Back
                  </button>

                  <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                    {allAnswered && idx < QUESTIONS.length - 1 && (
                      <button
                        style={{
                          padding: "11px 18px",
                          borderRadius: "3px",
                          border: "1px solid var(--b300)",
                          background: "transparent",
                          color: "var(--o900)",
                          fontSize: "13px",
                          fontFamily: "var(--font-dm), sans-serif",
                          cursor: "pointer",
                        }}
                        type="button"
                        onClick={finish}
                      >
                        Skip to results
                      </button>
                    )}
                    <button
                      disabled={typeof chosenIndex !== "number" || (idx === QUESTIONS.length - 1 && !allAnswered)}
                      style={{
                        padding: "11px 26px",
                        borderRadius: "3px",
                        border: "1px solid var(--o900)",
                        background: typeof chosenIndex === "number" ? "var(--o900)" : "var(--b200)",
                        borderColor: typeof chosenIndex === "number" ? "var(--o900)" : "var(--b200)",
                        color: typeof chosenIndex === "number" ? "var(--b50)" : "rgba(19,18,16,.4)",
                        fontSize: "13px",
                        fontFamily: "var(--font-dm), sans-serif",
                        cursor: typeof chosenIndex === "number" ? "pointer" : "not-allowed",
                      }}
                      type="button"
                      onClick={goNext}
                    >
                      {idx === QUESTIONS.length - 1 ? "See results →" : "Next →"}
                    </button>
                  </div>
                </div>

                {idx === QUESTIONS.length - 1 && !allAnswered && (
                  <p style={{ fontSize: "11.5px", color: "var(--amber)", marginTop: "12px" }}>
                    {QUESTIONS.length - answeredCount} question(s) still unanswered — the score is weighted across all{" "}
                    {QUESTIONS.length}, so fill the gaps before reading the result.
                  </p>
                )}
              </div>
            ) : (
              /* ── Results ──────────────────────────────────────────── */
              <div className="dq-results">
                <div className="dq-printonly" style={{ marginBottom: "22px", paddingBottom: "14px", borderBottom: "1px solid var(--b300)" }}>
                  <div style={{ fontFamily: "var(--font-fraunces), serif", fontSize: "20px", fontWeight: 200 }}>
                    DPDP readiness self-assessment — LexVio.ai
                  </div>
                  <div style={{ fontSize: "11px", color: "rgba(19,18,16,.6)", marginTop: "4px" }}>
                    Generated {printedAt ?? ""} · lexvio.ai/tools/dpdp-readiness-quiz · Provisions reviewed {RESEARCHED_ON}
                  </div>
                </div>

                {/* score panel */}
                <div
                  className="dq-scorepanel dq-dark-print"
                  style={{
                    background: "var(--o950)",
                    border: "1px solid rgba(251,250,246,.08)",
                    borderRadius: "6px",
                    padding: "clamp(24px,3vw,38px)",
                    color: "var(--b50)",
                  }}
                >
                  <div style={{ fontSize: "10px", letterSpacing: ".18em", textTransform: "uppercase", color: "var(--cl)", marginBottom: "16px" }}>
                    Self-assessed readiness
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: "14px", flexWrap: "wrap" }}>
                    <div style={{ fontFamily: "var(--font-fraunces), serif", fontSize: "clamp(56px,8vw,84px)", fontWeight: 200, lineHeight: 1, letterSpacing: "-.04em" }}>
                      {result.score}
                    </div>
                    <div style={{ fontSize: "16px", color: "rgba(251,250,246,.45)" }}>/ 100</div>
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: "11px",
                        letterSpacing: ".12em",
                        textTransform: "uppercase",
                        padding: "6px 14px",
                        borderRadius: "3px",
                        color: TONE_COLOR[band.tone],
                        border: `1px solid ${TONE_COLOR[band.tone]}`,
                      }}
                    >
                      {band.label}
                    </span>
                  </div>
                  <p style={{ fontSize: "14px", color: "rgba(251,250,246,.7)", lineHeight: 1.8, marginTop: "18px", maxWidth: "640px" }}>
                    {band.blurb}
                  </p>
                  <div style={{ fontSize: "11.5px", color: "rgba(251,250,246,.42)", marginTop: "16px", paddingTop: "14px", borderTop: "1px solid rgba(251,250,246,.08)", lineHeight: 1.7 }}>
                    Raw {result.raw} of {MAX_RAW} — each question scores 0 to 3 and is multiplied by a weight of 1, 2 or
                    3. The weight tracks what sits behind the provision in THE SCHEDULE, not how hard the item is to
                    build.
                  </div>
                </div>

                {/* band legend */}
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "14px" }}>
                  {BANDS.map((b) => (
                    <span
                      key={b.label}
                      style={{
                        fontSize: "11px",
                        padding: "6px 12px",
                        borderRadius: "3px",
                        border: `1px solid ${b.label === band.label ? TONE_COLOR[b.tone] : "var(--b200)"}`,
                        color: b.label === band.label ? TONE_COLOR[b.tone] : "rgba(19,18,16,.5)",
                        background: b.label === band.label ? "var(--b50)" : "transparent",
                      }}
                    >
                      {b.min}–{b.max} {b.label}
                    </span>
                  ))}
                </div>

                {/* categories */}
                <div style={{ background: "var(--b50)", border: "1px solid var(--b200)", borderRadius: "6px", padding: "clamp(22px,2.6vw,32px)", marginTop: "18px" }}>
                  <div className="ey" style={{ marginBottom: "18px" }}>By category</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    {cats.map((c) => {
                      const tone = toneForPct(c.pct);
                      return (
                        <div key={c.category} className="dq-catrow">
                          <div style={{ fontSize: "12.5px", color: "var(--o900)" }}>{c.category}</div>
                          <div style={{ height: "8px", background: "var(--b200)", borderRadius: "4px", overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${c.pct}%`, background: TONE_COLOR[tone] }} />
                          </div>
                          <div style={{ fontSize: "12px", textAlign: "right", color: TONE_COLOR[tone], fontVariantNumeric: "tabular-nums" }}>
                            {c.pct}%
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* remediation grouped by phase */}
                <div style={{ marginTop: "28px" }}>
                  <h2
                    style={{
                      fontFamily: "var(--font-fraunces), serif",
                      fontSize: "clamp(22px,2.6vw,30px)",
                      fontWeight: 200,
                      color: "var(--o900)",
                      letterSpacing: "-.01em",
                      marginBottom: "8px",
                    }}
                  >
                    What to fix, and when it bites
                  </h2>
                  <p style={{ fontSize: "13px", color: "rgba(19,18,16,.6)", lineHeight: 1.8, marginBottom: "22px", maxWidth: "680px" }}>
                    Grouped by the date the provision commences, so you can tell what has to be ready for November 2026
                    from what can wait for May 2027. Items you answered at the bottom two levels are marked as gaps;
                    third-level answers are marked partial.
                  </p>

                  {rems.length === 0 ? (
                    <div style={{ background: "var(--b50)", border: "1px solid var(--b200)", borderRadius: "6px", padding: "26px" }}>
                      <p style={{ fontSize: "14px", color: "rgba(19,18,16,.75)", lineHeight: 1.8 }}>
                        You selected the top option on every question. Nothing in this page has been verified against
                        your systems, contracts or evidence, so treat that as a statement of intent rather than a
                        finding — the useful next step is to have someone outside the team test three of these claims
                        against artefacts.
                      </p>
                    </div>
                  ) : (
                    byPhase.map((g) => (
                      <div key={g.phase.key} style={{ marginBottom: "30px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: "12px",
                            flexWrap: "wrap",
                            padding: "12px 16px",
                            background: g.phase.key === "phase2" ? "rgba(217,119,6,.07)" : "var(--b50)",
                            border: `1px solid ${g.phase.key === "phase2" ? "rgba(217,119,6,.28)" : "var(--b200)"}`,
                            borderRadius: "4px",
                            marginBottom: "12px",
                          }}
                        >
                          <strong style={{ fontFamily: "var(--font-fraunces), serif", fontWeight: 300, fontSize: "17px", color: "var(--o900)" }}>
                            {g.phase.date}
                          </strong>
                          <span style={{ fontSize: "12px", color: "rgba(19,18,16,.6)" }}>{g.phase.label}</span>
                          <span style={{ marginLeft: "auto", fontSize: "11px", color: "rgba(19,18,16,.5)" }}>
                            {g.items.length} item{g.items.length === 1 ? "" : "s"}
                          </span>
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {g.items.map((r) => (
                            <RemediationCard key={r.question.id} r={r} />
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* open items */}
                <div
                  style={{
                    background: "var(--b50)",
                    border: "1px solid var(--b200)",
                    borderLeft: "3px solid var(--amber)",
                    borderRadius: "4px",
                    padding: "22px 24px",
                    marginTop: "10px",
                  }}
                >
                  <div style={{ fontSize: "10px", letterSpacing: ".18em", textTransform: "uppercase", color: "var(--amber)", marginBottom: "10px" }}>
                    Open at the time of research ({RESEARCHED_ON})
                  </div>
                  <p style={{ fontSize: "13px", color: "rgba(19,18,16,.7)", lineHeight: 1.8, marginBottom: "12px" }}>
                    These could not be confirmed from primary sources. They are listed rather than guessed, because each
                    one could change what you should do next.
                  </p>
                  <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
                    {OPEN_ITEMS.filter((o) => o.consequential).map((o) => (
                      <li key={o.label} style={{ fontSize: "13px", color: "rgba(19,18,16,.78)", lineHeight: 1.75 }}>
                        <strong style={{ color: "var(--o900)" }}>{o.label}</strong>
                        <br />
                        <span style={{ color: "rgba(19,18,16,.62)" }}>{o.detail}</span>
                      </li>
                    ))}
                  </ul>
                  <p style={{ fontSize: "12px", color: "rgba(19,18,16,.5)", marginTop: "12px" }}>
                    A further {OPEN_ITEMS.filter((o) => !o.consequential).length} items are listed further down the page.
                  </p>
                </div>

                {/* actions */}
                <div className="dq-noprint" style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "24px" }}>
                  <button
                    style={{
                      padding: "12px 26px",
                      borderRadius: "3px",
                      border: "1px solid var(--o900)",
                      background: "var(--o900)",
                      color: "var(--b50)",
                      fontSize: "13px",
                      fontFamily: "var(--font-dm), sans-serif",
                      cursor: "pointer",
                    }}
                    type="button"
                    onClick={() => window.print()}
                  >
                    Print / save as PDF
                  </button>
                  <button
                    style={{
                      padding: "12px 22px",
                      borderRadius: "3px",
                      border: "1px solid var(--b300)",
                      background: "transparent",
                      color: "var(--o900)",
                      fontSize: "13px",
                      fontFamily: "var(--font-dm), sans-serif",
                      cursor: "pointer",
                    }}
                    type="button"
                    onClick={goBack}
                  >
                    ← Review answers
                  </button>
                  <button
                    style={{
                      padding: "12px 22px",
                      borderRadius: "3px",
                      border: "1px solid var(--b300)",
                      background: "transparent",
                      color: "rgba(19,18,16,.6)",
                      fontSize: "13px",
                      fontFamily: "var(--font-dm), sans-serif",
                      cursor: "pointer",
                    }}
                    type="button"
                    onClick={restart}
                  >
                    Start again
                  </button>
                </div>
                <p className="dq-noprint" style={{ fontSize: "11.5px", color: "rgba(19,18,16,.5)", marginTop: "10px", lineHeight: 1.7 }}>
                  Printing uses your browser&apos;s own print dialogue — choose &ldquo;Save as PDF&rdquo; as the
                  destination. Nothing is sent anywhere to produce the file.
                </p>

                <p style={{ fontSize: "11.5px", color: "rgba(19,18,16,.55)", lineHeight: 1.8, marginTop: "22px", paddingTop: "16px", borderTop: "1px solid var(--b200)" }}>
                  <strong style={{ color: "var(--o900)" }}>Disclaimer:</strong> {DISCLAIMER}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* ── Commencement phases ──────────────────────────────────────── */}
        <section className="dq-noprint" style={{ padding: "64px clamp(20px,4vw,52px)", borderBottom: "1px solid var(--b200)" }}>
          <div className="ey">Commencement</div>
          <h2
            style={{
              fontFamily: "var(--font-fraunces), serif",
              fontSize: "clamp(24px,2.8vw,34px)",
              fontWeight: 200,
              color: "var(--o900)",
              letterSpacing: "-.01em",
              marginBottom: "10px",
            }}
          >
            Three dates, not one
          </h2>
          <p style={{ fontSize: "14px", color: "rgba(19,18,16,.6)", lineHeight: 1.8, maxWidth: "760px", marginBottom: "26px" }}>
            G.S.R. 843(E) of 13 November 2025 appointed different commencement dates for different provisions of the Act,
            and Rule 1(2)–(4) of the DPDP Rules 2025 did the same for the Rules. Most published summaries collapse them
            into one deadline; the split below is the gazette position.
          </p>
          <div className="dq-phases">
            {PHASES.map((p) => (
              <div
                key={p.key}
                style={{
                  background: "var(--b50)",
                  border: "1px solid var(--b200)",
                  borderTop: `3px solid ${p.key === "phase1" ? "var(--b300)" : p.key === "phase2" ? "var(--amber)" : "var(--cu)"}`,
                  borderRadius: "5px",
                  padding: "22px 24px",
                }}
              >
                <div style={{ fontFamily: "var(--font-fraunces), serif", fontSize: "21px", fontWeight: 200, color: "var(--o900)", letterSpacing: "-.01em" }}>
                  {p.date}
                </div>
                <div style={{ fontSize: "12.5px", color: "var(--cu)", marginTop: "6px", lineHeight: 1.55 }}>{p.label}</div>
                <div style={{ fontSize: "9px", letterSpacing: ".16em", textTransform: "uppercase", color: "rgba(19,18,16,.45)", marginTop: "18px", marginBottom: "6px" }}>
                  Rules
                </div>
                <p style={{ fontSize: "12.5px", color: "rgba(19,18,16,.7)", lineHeight: 1.75 }}>{p.rules}</p>
                <div style={{ fontSize: "9px", letterSpacing: ".16em", textTransform: "uppercase", color: "rgba(19,18,16,.45)", marginTop: "16px", marginBottom: "6px" }}>
                  Act provisions
                </div>
                <p style={{ fontSize: "12.5px", color: "rgba(19,18,16,.7)", lineHeight: 1.75 }}>{p.whatApplies}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Penalties ────────────────────────────────────────────────── */}
        <section className="dq-noprint" style={{ padding: "64px clamp(20px,4vw,52px)", borderBottom: "1px solid var(--b200)", background: "var(--b100)" }}>
          <div style={{ maxWidth: "900px", margin: "0 auto" }}>
            <div className="ey">THE SCHEDULE, read with s.33</div>
            <h2
              style={{
                fontFamily: "var(--font-fraunces), serif",
                fontSize: "clamp(24px,2.8vw,34px)",
                fontWeight: 200,
                color: "var(--o900)",
                letterSpacing: "-.01em",
                marginBottom: "10px",
              }}
            >
              What a breach can cost
            </h2>
            <p style={{ fontSize: "14px", color: "rgba(19,18,16,.65)", lineHeight: 1.8, marginBottom: "22px" }}>
              {PENALTIES.status}
            </p>
            <div style={{ overflowX: "auto", background: "var(--b50)", border: "1px solid var(--b200)", borderRadius: "5px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", minWidth: "640px" }}>
                <thead>
                  <tr style={{ background: "var(--b100)", borderBottom: "1px solid var(--b200)" }}>
                    <th style={th}>#</th>
                    <th style={th}>Breach</th>
                    <th style={{ ...th, textAlign: "right" }}>Ceiling</th>
                  </tr>
                </thead>
                <tbody>
                  {PENALTIES.schedule.map((p) => (
                    <tr key={p.entry} style={{ borderBottom: "1px solid var(--b200)" }}>
                      <td style={{ ...td, fontFamily: "var(--font-fraunces), serif", fontSize: "15px", color: "var(--cu)" }}>{p.entry}</td>
                      <td style={td}>
                        <div style={{ color: "var(--o900)" }}>{p.breach}</div>
                        {p.note && <div style={{ fontSize: "11.5px", color: "rgba(19,18,16,.5)", marginTop: "4px", lineHeight: 1.6 }}>{p.note}</div>}
                      </td>
                      <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap", color: "var(--o900)" }}>{p.ceiling}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul style={{ fontSize: "13px", color: "rgba(19,18,16,.7)", lineHeight: 1.85, paddingLeft: "20px", marginTop: "20px" }}>
              {PENALTIES.mechanics.map((m, i) => (
                <li key={i} style={{ marginBottom: "6px" }}>
                  {m}
                </li>
              ))}
            </ul>
            {PENALTIES.unsupportedClaims.map((u, i) => (
              <p
                key={i}
                style={{
                  fontSize: "12.5px",
                  color: "rgba(19,18,16,.65)",
                  lineHeight: 1.8,
                  marginTop: "16px",
                  padding: "14px 16px",
                  background: "var(--b50)",
                  border: "1px solid var(--b200)",
                  borderLeft: "3px solid var(--red)",
                  borderRadius: "4px",
                }}
              >
                <strong style={{ color: "var(--o900)" }}>Unsupported:</strong> {u}
              </p>
            ))}
          </div>
        </section>

        {/* ── SDF ──────────────────────────────────────────────────────── */}
        <section className="dq-noprint" style={{ padding: "64px clamp(20px,4vw,52px)", borderBottom: "1px solid var(--b200)" }}>
          <div style={{ maxWidth: "900px", margin: "0 auto" }}>
            <div className="ey">Significant Data Fiduciary</div>
            <h2
              style={{
                fontFamily: "var(--font-fraunces), serif",
                fontSize: "clamp(24px,2.8vw,34px)",
                fontWeight: 200,
                color: "var(--o900)",
                letterSpacing: "-.01em",
                marginBottom: "12px",
              }}
            >
              {SDF_NOTE.headline}
            </h2>
            <p style={{ fontSize: "14.5px", color: "rgba(19,18,16,.72)", lineHeight: 1.85, marginBottom: "12px" }}>{SDF_NOTE.body}</p>
            <p style={{ fontSize: "13px", color: "var(--cu)", lineHeight: 1.8, marginBottom: "26px" }}>{SDF_NOTE.asAt}</p>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: "14px" }}>
              {SDF_NOTE.obligations.map((o) => (
                <div key={o.label} style={{ background: "var(--b50)", border: "1px solid var(--b200)", borderRadius: "5px", padding: "20px 22px" }}>
                  <div style={{ fontSize: "13px", fontWeight: 500, color: "var(--o900)", marginBottom: "8px" }}>{o.label}</div>
                  <p style={{ fontSize: "12.5px", color: "rgba(19,18,16,.68)", lineHeight: 1.75 }}>{o.text}</p>
                </div>
              ))}
            </div>

            <p style={{ fontSize: "13px", color: "rgba(19,18,16,.7)", lineHeight: 1.8, marginTop: "20px" }}>{SDF_NOTE.exposure}</p>
            <p
              style={{
                fontSize: "13px",
                color: "rgba(19,18,16,.7)",
                lineHeight: 1.8,
                marginTop: "12px",
                padding: "14px 16px",
                background: "var(--b100)",
                border: "1px solid var(--b200)",
                borderLeft: "3px solid var(--amber)",
                borderRadius: "4px",
              }}
            >
              <strong style={{ color: "var(--o900)" }}>Planning point:</strong> {SDF_NOTE.planningPoint}
            </p>
          </div>
        </section>

        {/* ── Open items ───────────────────────────────────────────────── */}
        <section className="dq-noprint" style={{ padding: "64px clamp(20px,4vw,52px)", borderBottom: "1px solid var(--b200)", background: "var(--b100)" }}>
          <div style={{ maxWidth: "900px", margin: "0 auto" }}>
            <div className="ey">Not confirmed</div>
            <h2
              style={{
                fontFamily: "var(--font-fraunces), serif",
                fontSize: "clamp(24px,2.8vw,34px)",
                fontWeight: 200,
                color: "var(--o900)",
                letterSpacing: "-.01em",
                marginBottom: "10px",
              }}
            >
              What we could not verify
            </h2>
            <p style={{ fontSize: "14px", color: "rgba(19,18,16,.65)", lineHeight: 1.8, marginBottom: "24px", maxWidth: "720px" }}>
              Everything else on this page traces to the gazette text of the Act, G.S.R. 843(E) or the DPDP Rules 2025.
              The items below could not be confirmed from a primary source as at {RESEARCHED_ON}, so they are listed as
              open rather than filled in with something plausible.
            </p>
            {OPEN_ITEMS.map((o) => (
              <details
                key={o.label}
                style={{ borderTop: "1px solid var(--b300)", padding: "16px 0" }}
              >
                <summary style={{ fontFamily: "var(--font-dm), sans-serif", fontSize: "14px", fontWeight: 500, color: "var(--o900)", cursor: "pointer", listStyle: "none", display: "flex", gap: "10px", alignItems: "baseline" }}>
                  <span
                    style={{
                      flexShrink: 0,
                      fontSize: "9px",
                      letterSpacing: ".14em",
                      textTransform: "uppercase",
                      color: o.consequential ? "var(--amber)" : "rgba(19,18,16,.4)",
                      border: `1px solid ${o.consequential ? "rgba(217,119,6,.3)" : "var(--b300)"}`,
                      borderRadius: "2px",
                      padding: "2px 7px",
                    }}
                  >
                    {o.consequential ? "Consequential" : "Open"}
                  </span>
                  <span>{o.label}</span>
                </summary>
                <p style={{ fontSize: "13px", color: "rgba(19,18,16,.7)", lineHeight: 1.8, marginTop: "10px" }}>{o.detail}</p>
              </details>
            ))}
            <div style={{ borderTop: "1px solid var(--b300)" }} />
          </div>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────────────── */}
        <section className="dq-noprint" style={{ padding: "64px clamp(20px,4vw,52px)", borderBottom: "1px solid var(--b200)" }}>
          <div style={{ maxWidth: "820px", margin: "0 auto" }}>
            <h2
              style={{
                fontFamily: "var(--font-fraunces), serif",
                fontSize: "clamp(24px,2.8vw,34px)",
                fontWeight: 200,
                color: "var(--o900)",
                marginBottom: "28px",
                letterSpacing: "-.01em",
              }}
            >
              Frequently asked
            </h2>
            {FAQS.map((f, i) => (
              <details key={i} style={{ borderTop: i === 0 ? "1px solid var(--b300)" : "none", borderBottom: "1px solid var(--b300)", padding: "18px 0" }}>
                <summary style={{ fontFamily: "var(--font-dm), sans-serif", fontSize: "15px", fontWeight: 500, color: "var(--o900)", cursor: "pointer", listStyle: "none" }}>
                  {f.q}
                </summary>
                <p style={{ fontSize: "14px", color: "rgba(19,18,16,.7)", lineHeight: 1.8, marginTop: "12px" }}>{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* ── Disclaimer ───────────────────────────────────────────────── */}
        <section className="dq-noprint" style={{ padding: "40px clamp(20px,4vw,52px)", borderBottom: "1px solid var(--b200)", background: "var(--b100)" }}>
          <p style={{ fontSize: "12px", color: "rgba(19,18,16,.55)", lineHeight: 1.7, maxWidth: "820px" }}>
            <strong style={{ color: "var(--o900)" }}>Disclaimer:</strong> {DISCLAIMER}
          </p>
        </section>

        {/* ── CTA ──────────────────────────────────────────────────────── */}
        <section className="dq-noprint" style={{ padding: "72px clamp(20px,4vw,52px)", textAlign: "center", background: "var(--o950)" }}>
          <h2
            style={{
              fontFamily: "var(--font-fraunces), serif",
              fontSize: "clamp(26px,3.2vw,38px)",
              fontWeight: 200,
              color: "var(--b50)",
              marginBottom: "14px",
            }}
          >
            The quiz finds the gaps.{" "}<br />
            <em style={{ color: "var(--cl)", fontStyle: "italic" }}>The scanner reads the contracts.</em>
          </h2>
          <p style={{ fontSize: "14px", color: "rgba(251,250,246,.45)", maxWidth: "560px", margin: "0 auto 32px", lineHeight: 1.75 }}>
            Paste a processor agreement or a DPA into the free scanner and LexVio flags the DPDP-shaped holes — missing
            security-safeguards flow-down, breach clocks that cannot feed a 72-hour Board report, GDPR erasure language
            that does not mirror s.8(7)(b).
          </p>
          <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
            <a className="cta-p" data-mag="true" href="/scan">
              <span className="cta-p-t">Try the scanner →</span>
            </a>
            <a className="cta-s" href={`/sign-up`}>
              Open LexVio →
            </a>
          </div>
        </section>
      </PageShell>
    </>
  );
}

// ── remediation card ───────────────────────────────────────────────────────

function RemediationCard({ r }: { r: Remediation }) {
  const color = r.severity === "gap" ? "var(--red)" : "var(--amber)";
  return (
    <div
      className="dq-rem"
      style={{
        background: "var(--b50)",
        border: "1px solid var(--b200)",
        borderLeft: `3px solid ${color}`,
        borderRadius: "4px",
        padding: "20px 22px",
      }}
    >
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center", marginBottom: "10px" }}>
        <span
          style={{
            fontSize: "9px",
            letterSpacing: ".14em",
            textTransform: "uppercase",
            color,
            border: `1px solid ${color}`,
            borderRadius: "2px",
            padding: "2px 8px",
          }}
        >
          {r.severity === "gap" ? "Gap" : "Partial"}
        </span>
        <span style={chip("var(--cu)", "rgba(184,110,44,.3)", "rgba(184,110,44,.07)")}>{r.question.category}</span>
        <span style={chip("rgba(19,18,16,.55)", "var(--b200)", "transparent")}>Weight ×{r.question.weight}</span>
        <span style={{ marginLeft: "auto", fontSize: "11px", color: "rgba(19,18,16,.45)" }}>
          {r.chosen.score} / 3
        </span>
      </div>

      <div style={{ fontFamily: "var(--font-fraunces), serif", fontWeight: 200, fontSize: "17px", lineHeight: 1.45, color: "var(--o900)", letterSpacing: "-.01em" }}>
        {r.question.question}
      </div>

      <div style={{ fontSize: "11.5px", color: "var(--cu)", marginTop: "10px", lineHeight: 1.6 }}>{r.question.citation}</div>
      <div style={{ fontSize: "11.5px", color: "rgba(19,18,16,.5)", marginTop: "3px", lineHeight: 1.6 }}>{r.question.phase}</div>

      <div style={{ fontSize: "12.5px", color: "rgba(19,18,16,.6)", marginTop: "12px", lineHeight: 1.7 }}>
        <span style={{ fontSize: "9px", letterSpacing: ".16em", textTransform: "uppercase", color: "rgba(19,18,16,.45)" }}>
          You answered
        </span>
        <br />
        {r.chosen.label}
      </div>

      <p style={{ fontSize: "13px", color: "rgba(19,18,16,.78)", marginTop: "12px", lineHeight: 1.8, paddingTop: "12px", borderTop: "1px solid var(--b200)" }}>
        {r.question.guidance}
      </p>
    </div>
  );
}

// ── shared inline styles ───────────────────────────────────────────────────

function chip(color: string, border: string, background: string): React.CSSProperties {
  return {
    fontSize: "9px",
    letterSpacing: ".14em",
    textTransform: "uppercase",
    color,
    border: `1px solid ${border}`,
    background,
    borderRadius: "2px",
    padding: "3px 9px",
  };
}

const th: React.CSSProperties = {
  padding: "13px 18px",
  textAlign: "left",
  fontSize: "9px",
  letterSpacing: ".14em",
  textTransform: "uppercase",
  color: "rgba(19,18,16,.5)",
  fontWeight: 500,
};

const td: React.CSSProperties = {
  padding: "14px 18px",
  verticalAlign: "top",
  color: "var(--o900)",
  fontFamily: "var(--font-dm), sans-serif",
  lineHeight: 1.6,
};
